import {
  capitalize,
  cloneDeep,
  isObjectLike,
} from 'lodash';
import * as vanConstants from '@/constants/products/van';
import * as commonConstants from '@/constants/common';
import {
  regNumberRegex,
} from '@/helpers/products/car';
import {
  nameRegex,
} from '@/helpers/common';

import {
  DATE_FORMAT,
  UtcDate,
} from '@/helpers/date';
import { getClaimsRules, getConvictionsRules } from '@/helpers/products/van';
import * as vanPayloadTemplates from '@/payloadTemplates/van';
import { validate } from 'vee-validate';

const constants = Object.assign(
  {},
  commonConstants,
  vanConstants,
);

const currentYear = new UtcDate().getFullYear();

export const initVehicleRules = (options) => (step = {}, data) => ({
  vehicleConfirm: {
    required: true,
    integer: true,
    digits: 8,
  },
  vehicleMake: {
    required: true,
    alphaNum: true,
    length: 2,
  },
  vehicleMakeLabel: {
    required: true,
    typeof: 'string',
  },
  vehicleModel: {
    required: true,
    typeof: 'string',
  },
  vehicleModelLabel: {
    required: true,
    typeof: 'string',
  },
  engineSize: {
    required: true,
    typeof: 'string',
  },
  vehicleWeight: {
    required: true,
    typeof: 'number',
  },
  yearOfManufacture: {
    required: true,
    date_format: 'y',
  },
  vehicleRegistrationNumber: {
    regex: regNumberRegex,
    typeof: 'string',
  },
  vehicleSide: {
    required: true,
    typeof: 'boolean',
  },
  seatsOfVehicle: {
    required: true,
    in: {
      items: constants.VEHICLE_SEATS_QTY,
    },
  },
  vehicleAlarm: {
    required: true,
    in: {
      items: constants.VEHICLE_ALARM_IMMOBILISER,
    },
  },
  vehicleTrackingDevice: {
    required: true,
    typeof: 'boolean',
  },
  vehicleGreyOrImport: {
    required: true,
    typeof: 'boolean',
  },
  estimatedValue: {
    required: true,
    integer: true,
    between: [0, 99999999],
    minValue: 50,
  },
  vehicleModifiedFromManufacturer: {
    required: true,
    typeof: 'boolean',
  },
  vehicleModifications: {
    requiredExtended: {
      customValidator: value => {
        if (!isObjectLike(value)) return false;
        if (step.vehicleModifiedFromManufacturer === true) return Object.keys(value).length > 0;
        return true;
      },
    },
  },
  bodyType: {
    required: true,
    in: {
      items: constants.VEHICLE_BODY_TYPES,
    },
  },
  vehiclePurchased: {
    required: true,
    typeof: 'boolean',
  },
  vehicleBought: (() => {
    const rules = {
      switchedBy: {
        condition: step.vehiclePurchased,
      },
    };
    if (step.vehiclePurchased) {
      return {
        ...rules,
        date_format: DATE_FORMAT,
        before: {
          equal: true,
          compare: 'today',
        },
      };
    }
    return rules;
  })(),
  ownerOfVehicle: {
    required: true,
    in: {
      items: constants.COVER_OWNER_OF_VEHICLE_PREVALIDATION,
    },
  },
  registeredOfVehicle: {
    required: true,
    in: {
      items: constants.COVER_REGISTERED_OF_VEHICLE_PREVALIDATION,
    },
  },
  vehicleKept: {
    required: true,
    in: {
      items: constants.VEHICLE_KEPT,
    },
  },
  typeOfUse: {
    required: true,
    in: {
      items: constants.VEHICLE_TYPE_OF_USE,
    },
  },
  annualMileage: {
    required: true,
    integer: true,
    between: [1, 999999],
  },
  businessMileage: {
    required: step.typeOfUse && constants.VEHICLE_TYPE_OF_USE_BUSINESS.includes(step.typeOfUse),
    integer: true,
    between: [1, step.annualMileage || 999999],
  },
  vehicleRacking: {
    required: true,
    typeof: 'boolean',
  },
  carryingHazardousGoods: {
    required: true,
    typeof: 'boolean',
  },
  hazardousSites: {
    required: true,
    typeof: 'boolean',
  },
  badDriver: {
    required: true,
    typeof: 'boolean',
  },
  vehicleSignage: {
    required: true,
    in: {
      items: constants.VEHICLE_SIGNWRITTENS,
    },
  },
  typeOfCompany: {
    required: true,
    in: {
      items: constants.VEHICLE_COMPANY_TYPES,
    },
  },
  businessEstablished: {
    required: step.typeOfUse && constants.VEHICLE_TYPE_OF_USE_BUSINESS.includes(step.typeOfUse),
    integer: true,
    between: [currentYear - constants.BUSINESS_ESTABLISHED_MAX_YEARS, currentYear], // Business should be established in past 200 years
  },
  registrationDate: {
    date_format: DATE_FORMAT,
    before: {
      compare: 'today',
    },
  },
});

export const initDriversRules = (options) => (step = {}, data) => ({
  title: {
    in: {
      items: constants.TITLES,
    },
  },
  firstName: {
    name: [2, 50],
    regex: nameRegex,
  },
  surname: {
    name: [2, 50],
    regex: nameRegex,
  },
  dateOfBirth: {
    date_format: DATE_FORMAT,
    dateOfBirth: true,
  },
  bornInUk: {
    required: true,
    typeof: 'boolean',
  },
  dateOfUkResident: (() => {
    const rules = {
      switchedBy: {
        condition: !step.bornInUk,
      },
    };
    if (!step.bornInUk) {
      rules.date_format = DATE_FORMAT;
      rules.after = {
        compare: step.dateOfBirth,
        equal: true,
      };
      rules.before = {
        compare: 'today',
        equal: true,
      };
    }
    return rules;
  })(),
  maritalStatus: {
    in: {
      items: constants.DRIVER_MARITAL_STATUS,
    },
  },
  children: {
    required: true,
    in: {
      items: constants.DRIVER_CHILDREN,
    },
  },
  telephone: {
    telephone: [10, 12],
  },
  email: {
    email: true,
    required: true,
  },
  postcode: {
    postcode: true,
  },
  houseNumberOrName: {
    typeof: 'string',
  },
  addressLine2: {
    typeof: 'string',
  },
  addressLine3: {
    typeof: 'string',
  },
  addressLine4: {
    typeof: 'string',
  },
  keptAddress: {
    required: true,
    typeof: 'boolean',
  },
  keptPostcode: {
    postcode: !step.keptAddress,
  },
  residentialStatus: {
    required: true,
    in: {
      items: constants.DRIVER_RESIDENTIAL_STATUS,
    },
  },
  employmentStatus: {
    required: true,
    in: {
      items: constants.DRIVER_EMPLOYMENT_STATUS,
    },
  },
  occupationList: {
    required: constants.DRIVER_EMPLOYMENT_STATUS_BUSINESS.includes(step.employmentStatus),
    alphaNum: true,
    length: 3,
  },
  occupationKey: {
    required: constants.DRIVER_EMPLOYMENT_STATUS_BUSINESS.includes(step.employmentStatus),
    typeof: 'string',
  },
  businessList: {
    required: constants.DRIVER_EMPLOYMENT_STATUS_BUSINESS.includes(step.employmentStatus),
    alphaNum: true,
    length: 3,
  },
  businessKey: {
    required: constants.DRIVER_EMPLOYMENT_STATUS_BUSINESS.includes(step.employmentStatus),
    typeof: 'string',
  },
  partTimeOccupation: {
    required: true,
    typeof: 'boolean',
  },
  occupationListPt: {
    required: step.partTimeOccupation,
    alphaNum: true,
    length: 3,
  },
  occupationKeyPt: {
    required: step.partTimeOccupation,
  },
  businessListPt: {
    required: step.partTimeOccupation,
  },
  businessKeyPt: {
    required: step.partTimeOccupation,
  },
  typeOfDriving: {
    required: true,
    in: {
      items: constants.DRIVER_TYPE_OF_DRIVING,
    },
  },
  periodOfLicenceHeld: {
    periodOfLicenceHeld: {
      dateOfBirth: step.dateOfBirth,
      typeOfDriving: step.typeOfDriving,
      driverTypeOfDriving: constants.DRIVER_TYPE_OF_DRIVING,
    },
  },
  licenceNumber: {
    licenceNumber: {
      partial: true,
    },
  },
  licenceNumberYesNo: {
    required: true,
    typeof: 'boolean',
  },
  licenceNumberDetailsSeq1: {
    regex: /^[A-Za-z]+9*?$/,
    required: true,
    length: 5,
  },
  licenceNumberDetailsSeq2: {
    integer: true,
    required: true,
    length: 6,
  },
  licenceNumberDetailsSeq3: {
    regex: /^[A-Za-z\d]+$/,
    required: true,
    length: 3,
  },
  licenceNumberDetailsSeq4: {
    regex: /^[A-Za-z\d]+$/,
    required: true,
    length: 2,
  },
  medicalConditions: {
    in: {
      items: constants.DRIVER_MEDICAL_CONDITIONS,
    },
  },
  advancedMotorist: {
    switchedBy: {
      condition: step.typeOfDriving === 'F',
    },
    typeof: 'boolean',
  },
  hadAnyAccidents: {
    required: true,
    typeof: 'boolean',
  },
  claims: {
    required: step.hadAnyAccidents,
  },
  hadAnyConvictions: {
    required: true,
    typeof: 'boolean',
  },
  convictions: {
    required: step.hadAnyConvictions,
  },
  hadAnyNonMotConvictions: {
    required: true,
    typeof: 'boolean',
  },
  relationshipProposer: {
    in: {
      items: constants.ADDITIONAL_RELATIONSHIP_PROPOSER,
    },
  },
  useOfOtherVehicles: {
    in: {
      items: constants.COVER_USE_OF_OTHER_VEHICLES,
    },
  },
});

export const initCoverRules = (options) => (step = {}, data) => ({
  dateOfInsuranceStart: {
    date_format: DATE_FORMAT,
    after: {
      equal: true,
      compare: 'today',
    },
    before: {
      equal: true,
      compare: new UtcDate().addDays(constants.DATE_OF_INSURANCE_START_MAX_DAYS).format(),
    },
  },
  typeOfCover: {
    in: {
      items: constants.COVER_TYPE_OF_COVER,
    },
  },
  voluntaryExcess: {
    in: {
      items: constants.COVER_VOLUNTARY_EXCESS,
    },
  },
  noClaimsBonus: {
    in: {
      items: constants.COVER_NO_CLAIMS_BONUS,
    },
    maxValue: Number(constants.COVER_NO_CLAIMS_BONUS_MAX),
    minValue: Number(constants.COVER_NO_CLAIMS_BONUS_MIN),
  },
  useOfOtherVehicles: {
    in: {
      items: constants.COVER_USE_OF_OTHER_VEHICLES,
    },
  },
  noClaimsBonusOtherVehicle: {
    in: {
      items: constants.COVER_NO_CLAIMS_BONUS,
      required: step.useOfOtherVehicles && constants.COVER_NO_CLAIMS_BONUS_OTHER_REQUIRED.includes(step.useOfOtherVehicles),
    },
  },
  mainUserOfVehicle: {
    in: {
      items: Array.from({ length: data.drivers ? data.drivers.length : 1 }, (_, i) => i + 1),
      required: data.drivers ? data.drivers.length > 1 : false,
    },
  },
  protectBonus: {
    required: step.noClaimsBonus >= 4,
    typeof: 'boolean',
  },
  numberOfOtherVehicles: {
    in: {
      items: constants.COVER_NUMBER_OF_OTHER_VEHICLES,
    },
  },
  payInsuranceMonthly: {
    required: true,
    typeof: 'boolean',
  },
  insuranceEverDeclined: {
    required: true,
    typeof: 'boolean',
  },
  outbound: {
    required: true,
    typeof: {
      typecheck: 'boolean',
    },
  },
  directMarketingEmail: {
    typeof: {
      typecheck: 'boolean',
    },
  },
  directMarketingPhone: {
    typeof: {
      typecheck: 'boolean',
    },
  },
  directMarketingPost: {
    typeof: {
      typecheck: 'boolean',
    },
  },
  directMarketingSms: {
    typeof: {
      typecheck: 'boolean',
    },
  },
  userKeepSignIn: {
    required: !options.config.hiddenQuestions['user-keep-me-sign-in'],
    typeof: 'boolean',
  },
});

export const createVanRules = (data, config) => {
  const validationHelpers = {};

  // isDriverPassNeeded, computed, Step2.vue
  validationHelpers.isDriverPassNeeded = false;
  if (data.drivers && data.drivers[0]) {
    const periodOfLicenceHeldDate = new UtcDate(data.drivers[0].periodOfLicenceHeld);
    if (periodOfLicenceHeldDate.isValid()) {
      validationHelpers.isDriverPassNeeded = periodOfLicenceHeldDate.differenceInYears(new UtcDate()) === 0;
    }
  }

  // yearsOfLicenceHeld, computed, Step3.vue
  validationHelpers.yearsOfLicenceHeld = (data.drivers && data.drivers[0] && data.drivers[0].periodOfLicenceHeld) ? new UtcDate().differenceInYears(new UtcDate(data.drivers[0].periodOfLicenceHeld)) : null;

  validationHelpers.config = config;

  return {
    getVehicleRules: initVehicleRules(validationHelpers),
    getDriversRules: initDriversRules(validationHelpers),
    getCoverRules: initCoverRules(validationHelpers),
    getClaimsRules,
    getConvictionsRules,
  };
};

export const prevalidate = async (dataRaw, config) => {
  const data = cloneDeep(dataRaw);
  const rules = createVanRules(data, config);

  await Promise.all(
    ['vehicle', 'drivers', 'cover'].map(async stepId => {
      if (!data[stepId]) return;

      if (Array.isArray(data[stepId]) && stepId === 'drivers') {
        return await Promise.all(data[stepId].map(async (driver, index) => {
          const driverTemplate = vanPayloadTemplates[`DRIVER${index === 0 ? '' : '_ADDITIONAL'}`];
          const driverFieldsSorted = Object.keys(driverTemplate).sort();
          const driverRules = rules[`get${capitalize(stepId)}Rules`](driver, data);

          const driverValidity = await Promise.all(driverFieldsSorted.map(field => validate(driver[field], driverRules[field])));

          driverFieldsSorted.forEach((field, i) => {
            if (!driverValidity[i].valid) {
              data[stepId][index][field] = driverTemplate[field];
            }
          });

          // claims and convictions
          if (driver.hadAnyAccidents === true && Array.isArray(driver.claims) && driver.claims.length > 0) {
            const claimTemplate = vanPayloadTemplates.CLAIM;
            const claimFieldsSorted = Object.keys(claimTemplate).sort();

            await Promise.all(driver.claims.map(async (claim, claimIndex) => {
              const claimRules = rules.getClaimsRules(claim);
              const claimValidity = await Promise.all(claimFieldsSorted.map(field => validate(claim[field], claimRules[field])));

              claimFieldsSorted.forEach((field, i) => {
                if (!claimValidity[i].valid) {
                  driver.claims[claimIndex][field] = claimTemplate[field];
                }
              });
              return claimValidity;
            }));

            driver.claims = driver.claims.filter(claim => claim.dateOfClaim !== null);
            if (driver.claims.length === 0) driver.hadAnyAccidents = false;
          } else {
            driver.claims = [];
          }

          if (driver.hadAnyConvictions === true && Array.isArray(driver.convictions) && driver.convictions.length > 0) {
            const convictionTemplate = vanPayloadTemplates.CONVICTION;
            const convictionFieldsSorted = Object.keys(convictionTemplate).sort();

            await Promise.all(driver.convictions.map(async (conviction, convictionIndex) => {
              const convictionRules = rules.getConvictionsRules(conviction);
              const convictionValidity = await Promise.all(convictionFieldsSorted.map(field => validate(conviction[field], convictionRules[field])));

              convictionFieldsSorted.forEach((field, i) => {
                if (!convictionValidity[i].valid) {
                  driver.convictions[convictionIndex][field] = convictionTemplate[field];
                }
              });
              return convictionValidity;
            }));

            driver.convictions = driver.convictions.filter(conviction => conviction.dateOfConviction !== null);
            if (driver.convictions.length === 0) driver.hadAnyConvictions = false;
          } else {
            driver.convictions = [];
          }
        }));
      } else if (isObjectLike(data[stepId])) {
        const stepTemplate = vanPayloadTemplates[stepId.toUpperCase()];
        const stepFieldsSorted = Object.keys(stepTemplate).sort();
        const stepRules = rules[`get${capitalize(stepId)}Rules`](data[stepId], data);

        const stepValidity = await Promise.all(stepFieldsSorted.map(field => {
          return validate(data[stepId][field], stepRules[field]);
        }));

        stepFieldsSorted.forEach((field, i) => {
          if (!stepValidity[i].valid) {
            data[stepId][field] = stepTemplate[field];
          }
        });
      }
    }),
  );

  return data;
};
